import { useEffect, useRef } from "react";
import Navbar from "../components/layout/Navbar3";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default function TermsOfServicePage() {
  const heroRef = useRef(null);
  const titleRef = useRef(null);
  const contentRef = useRef(null);
  const imageRef = useRef(null);
  const blob1Ref = useRef(null);
  const blob2Ref = useRef(null);
  const blob3Ref = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        titleRef.current,
        { y: 50, opacity: 0 },
        { y: 0, opacity: 1, duration: 1.2, ease: "power4.out", delay: 0.2 }
      );

      gsap.fromTo(
        ".terms-section",
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.08,
          ease: "power3.out",
          scrollTrigger: {
            trigger: contentRef.current,
            start: "top 80%",
          },
        }
      );

      gsap.fromTo(
        imageRef.current,
        { x: 60, opacity: 0, scale: 0.95 },
        {
          x: 0,
          opacity: 1,
          scale: 1,
          duration: 1.2,
          ease: "power3.out",
          delay: 0.4,
        }
      );
    });

    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      if (blob1Ref.current) blob1Ref.current.style.transform = `translate3d(${x * 30}px, ${y * 30}px, 0)`;
      if (blob2Ref.current) blob2Ref.current.style.transform = `translate3d(${x * -40}px, ${y * 20}px, 0)`;
      if (blob3Ref.current) blob3Ref.current.style.transform = `translate3d(${x * 20}px, ${y * -30}px, 0)`;
    };

    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => {
      ctx.revert();
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  const terms = [
    {
      num: "01",
      title: "Accounts",
      content: "To use our Service, you may be required to create an account. You must provide accurate, complete, and current information. Failure to do so constitutes a breach of the Terms, which may result in termination of your account. You are responsible for safeguarding the password you use and for any activities under your account.",
    },
    {
      num: "02",
      title: "Subscriptions and Payment",
      content: "Some parts of the Service are billed on a subscription basis. You will be billed in advance on a recurring basis (e.g., monthly or annually). You must provide accurate billing information and notify us of any changes. We reserve the right to change subscription fees with prior notice. Continued use of the Service after a change constitutes your acceptance of the new pricing.",
    },
    {
      num: "03",
      title: "Free Trial",
      content: "We may offer a free trial for a limited time. If you do not cancel before the end of the trial period, you will be charged for the applicable subscription.",
    },
    {
      num: "04",
      title: "Cancellation and Refunds",
      content: "You may cancel your subscription at any time through your account settings. Cancellations will take effect at the end of your current billing period. Refunds are not provided for partial billing periods or unused features, except where required by law.",
    },
    {
      num: "05",
      title: "Use of Service",
      content: (
        <>
          <p className="mb-4">You agree to use the Service only for lawful purposes and in accordance with these Terms. You may not:</p>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <span className="w-5 h-5 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
              </span>
              <span className="text-gray-400">Use the Service to transmit unlawful material</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-5 h-5 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
              </span>
              <span className="text-gray-400">Reverse engineer or attempt to extract the source code of our software</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-5 h-5 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
              </span>
              <span className="text-gray-400">Use automated tools to access or disrupt the Service</span>
            </li>
          </ul>
        </>
      ),
    },
    {
      num: "06",
      title: "Intellectual Property",
      content: "All content and materials provided by the Service, including but not limited to software, logos, and designs, are the property of MNR Technologies Global Pvt Ltd or its licensors and are protected by intellectual property laws. You are granted a limited, non-exclusive, non-transferable license to use the Service in accordance with these Terms.",
    },
    {
      num: "07",
      title: "Termination",
      content: "We may suspend or terminate your account if you violate these Terms or engage in fraudulent, abusive, or unlawful activity. You may stop using the Service at any time.",
    },
    {
      num: "08",
      title: "Limitation of Liability",
      content: "To the maximum extent permitted by law, MNR Technologies Global Pvt Ltd shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues.",
    },
    {
      num: "09",
      title: "Governing Law",
      content: "These Terms shall be governed and construed in accordance with the laws of India/Telangana, without regard to its conflict of law provisions.",
    },
    {
      num: "10",
      title: "Changes to Terms",
      content: "We reserve the right to modify or replace these Terms at any time. If a revision is material, we will provide notice prior to any new terms taking effect. By continuing to access or use our Service after changes become effective, you agree to be bound by the revised terms.",
    },
    {
      num: "11",
      title: "Contact Us",
      content: (
        <>
          If you have any questions about these Terms, please contact us at:{" "}
          <a href="mailto:info@mnrtechnologies.com" className="text-blue-400 hover:text-blue-300 transition-colors border-b border-blue-400/30 hover:border-blue-400">
            info@mnrtechnologies.com
          </a>
        </>
      ),
    },
  ];

  return (
    <div className="bg-slate-950 text-white min-h-screen relative overflow-hidden selection:bg-blue-500/30">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=Playfair+Display:ital,wght@0,400;0,600;1,400;1,600&display=swap');
        .font-playfair { font-family: 'Playfair Display', serif; }
        .font-inter { font-family: 'Inter', sans-serif; }
        .text-gradient-mask {
          background: linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.65) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .noise-overlay {
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.02'/%3E%3C/svg%3E");
        }
      `}</style>

      <div className="fixed inset-0 noise-overlay pointer-events-none z-[1] opacity-30" />
      <div ref={blob1Ref} className="fixed -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-blue-600/15 blur-[130px] opacity-60 pointer-events-none z-0 will-change-transform" />
      <div ref={blob2Ref} className="fixed bottom-[-100px] right-[-100px] w-[500px] h-[500px] rounded-full bg-purple-600/15 blur-[110px] opacity-50 pointer-events-none z-0 will-change-transform" />
      <div ref={blob3Ref} className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] rounded-full bg-indigo-600/10 blur-[150px] opacity-40 pointer-events-none z-0 will-change-transform" />

      <Navbar />

      <div className="relative z-10">
        <section ref={heroRef} className="pt-28 pb-16 px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-4xl mx-auto">
            <p className="text-xs font-medium text-gray-600 uppercase tracking-[0.3em] mb-6">
              Legal
            </p>
            <h1
              ref={titleRef}
              className="font-inter text-4xl sm:text-5xl md:text-7xl font-black tracking-tight text-gradient-mask opacity-0"
            >
              Terms of<br />
              <span className="font-playfair italic font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500">
                Service
              </span>
            </h1>
            <p className="mt-6 text-lg text-gray-500 max-w-2xl mx-auto font-light leading-relaxed">
              The rules that keep our platform fair, secure, and reliable for everyone. Please read carefully.
            </p>
          </div>
        </section>

        <div className="relative py-6 flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />
          </div>
          <div className="relative z-10 w-2 h-2 rounded-full bg-blue-500/40 shadow-[0_0_12px_rgba(59,130,246,0.4)] animate-pulse" />
        </div>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-start">
            <div ref={contentRef} className="flex-1 order-2 lg:order-1">
              <div className="mb-10">
                <p className="text-gray-400 leading-relaxed text-lg font-light">
                  Please read these Terms of Service carefully before using the PPPVue service operated by MNR Technologies Global Pvt Ltd. By accessing or using the Service, you agree to be bound by these Terms. If you disagree with any part of the terms, then you may not access the Service.
                </p>
              </div>

              <div className="space-y-10">
                {terms.map((term, index) => (
                  <div
                    key={index}
                    className="terms-section group relative pl-8 border-l border-white/[0.06] hover:border-white/[0.12] transition-colors duration-500"
                  >
                    <span className="absolute left-0 top-0 -translate-x-1/2 font-inter text-[10px] font-bold text-gray-700 bg-slate-950 px-1.5 py-0.5 rounded">
                      {term.num}
                    </span>
                    <h2 className="font-inter text-xl font-semibold text-white mb-3 tracking-tight">
                      {term.title}
                    </h2>
                    <div className="text-gray-400 leading-relaxed font-light">
                      {term.content}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-16 pt-8 border-t border-white/[0.06]">
                <p className="text-sm text-gray-600">
                  Effective date: January 2024. These terms are subject to change. For questions, contact{" "}
                  <a href="mailto:info@mnrtechnologies.com" className="text-blue-400 hover:text-blue-300 transition-colors border-b border-blue-400/30 hover:border-blue-400">
                    info@mnrtechnologies.com
                  </a>
                </p>
              </div>
            </div>

            <div ref={imageRef} className="flex-1 order-1 lg:order-2 lg:sticky lg:top-28 opacity-0">
              <div className="relative group">
                <div className="absolute -inset-2 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-500" />
                <img
                  src="/Terms.jpg"
                  alt="Legal documents"
                  width="500"
                  height="700"
                  loading="eager"
                  decoding="async"
                  className="relative w-full h-auto object-cover rounded-2xl border border-white/10 shadow-2xl shadow-black/40"
                />
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}